import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { GET_USER, useQueryUser } from '@/hooks/query/useQueryUser';
import {
  useIsImprovedCopyActionsEnabledForEditor,
  useIsSectionsInNotesEnabledForEditor,
} from '@/hooks/useMarkdownSectionalEditorFlags';
import { logger } from '@/utils/logger/logger';
import { useIsMarkdownNativeEditorEnabled } from '@heidi/session/flags';
import { type UserResponse } from '@heidi/workspace/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
  copySettingsToWire,
  enumToToggles,
  wireToCopySettings,
} from './useCopySettings.helpers';
import { type CopySettings, type CopySettingsWire } from './useCopySettings.types';

// Fallback only — the close-flush is the real coalesce point. 1s covers the
// gap between deliberate clicks. Mirrors `useDebouncedLinkedSessionsUpdate`.
const PERSIST_DEBOUNCE_MS = 1000;

/**
 * Default toggle state shown while the user query is loading or the
 * cached value is absent. Mirrors the BE default for `default_copy_type`
 * (`COPY_WITH_FORMATTING_WITHOUT_LISTS`), except `headings` defaults off:
 * a section H1 copies/exports as plain body text unless the user opts in.
 */
const LOADING_FALLBACK: CopySettings = {
  headings: false,
  boldAndItalics: true,
  bulletPoints: false,
  tables: true,
};

/**
 * Local widening of the response type so we can read `copy_settings`
 * without waiting for SCR-4468 to deploy + `pnpm run schema:dev`.
 *
 * TODO(SCR-4468): drop this once the generated schema includes the
 * field.
 */
type SessionSettingsWithCopy = NonNullable<UserResponse['session_settings']> & {
  copy_settings?: CopySettingsWire | null;
};

/**
 * Applied while the Bullet points and Tables rows are hidden: a stored `false`
 * for either would strip content the user can no longer turn back on, and the
 * whole never-opened-the-panel cohort stores `bullet_points: false` via the
 * legacy `default_copy_type` default. Forcing both on at read time keeps copy,
 * export, print and per-section copy on the one effective value. Read-time
 * only: a stored preference is never rewritten, so it returns intact if the
 * toggles come back.
 */
const FORCED_ON_WHILE_HIDDEN: Pick<CopySettings, 'bulletPoints' | 'tables'> = {
  bulletPoints: true,
  tables: true,
};

export type UseCopySettingsResult = {
  /** Four-toggle FE view of the user's copy preference. */
  settings: CopySettings;
  /**
   * Patch the user's `copy_settings`. **Only writes when the
   * `release-scribe-improve-copy-actions` flag is on** — the legacy
   * `default_copy_type` write path stays in `useSessionCopy` so
   * existing UI keeps working unchanged when the flag is off.
   */
  updateSettings: (next: Partial<CopySettings>) => void;
  isImprovedCopyEnabled: boolean;
  /**
   * When true the panel omits the Bullet points and Tables rows, and
   * `settings` reports both as on regardless of what is stored. The two go
   * together by design — see {@link FORCED_ON_WHILE_HIDDEN}.
   */
  areListAndTableTogglesHidden: boolean;
};

/**
 * Resolves the user's copy preference as a four-toggle object and (when
 * gated on `release-scribe-improve-copy-actions`) lets the new UI write
 * back to `session_settings.copy_settings`.
 *
 * FF off — `settings` is derived from the legacy `default_copy_type`
 * enum via `enumToToggles`. `updateSettings` is a no-op; the legacy
 * radio UI continues to drive writes via `useSessionCopy.updateCopyPreference`.
 *
 * FF on — `settings` reads from `copy_settings` when present
 * (post-SCR-4468). Until BE deploys, falls back to
 * `enumToToggles(default_copy_type)` so the toggles always reflect the
 * user's stored preference. `updateSettings` optimistically merges
 * `copy_settings` into the user cache in the same tick, then persists
 * the result with a debounced PATCH so rapid toggling doesn't flicker.
 */
export const useCopySettings = (): UseCopySettingsResult => {
  // MOBILE-PARITY-DEBT(SCR-4468): B3 — this client-evaluated flag decides which
  // field is authoritative for the user's copy preference: ON writes
  // `session_settings.copy_settings`, OFF leaves the legacy `default_copy_type`
  // enum in charge (see `updateSettings` below). No mobile ticket: as of Aug 2026
  // neither mobile app reads or writes either field, so there is no rule to
  // mirror. Remove when SCR-4468 lands and the backend resolves the shape.
  const isImprovedCopyEnabled = useIsImprovedCopyActionsEnabledForEditor();
  const isSectionsInNotesEnabled = useIsSectionsInNotesEnabledForEditor();
  const isMarkdownNativeEditorEnabled = useIsMarkdownNativeEditorEnabled();
  const queryClient = useQueryClient();

  // Only the legacy vMarkdown editor loses the two rows: sections there strip
  // lists and tables anyway, so the toggles control nothing. The markdown-native
  // editor renders both natively, so its users keep the toggles — and, because
  // the rows stay reachable, keep whatever they have stored.
  const areListAndTableTogglesHidden =
    isImprovedCopyEnabled && isSectionsInNotesEnabled && !isMarkdownNativeEditorEnabled;

  const { data: sessionSettings } = useQueryUser(
    (data) => data?.session_settings as SessionSettingsWithCopy | undefined
  );
  // No success refetch: the optimistic cache already holds the value we
  // sent, and a refetch would revert the toggles until SCR-4468's BE ships.
  // `onError` still invalidates, so rollback is preserved.
  const { mutate: updateUser } = useMutateUpdateUser({ enableInvalidation: false });

  // Debounced persist: rapid toggles collapse to one PATCH, so no refetch
  // lands mid-burst and clobbers a later toggle (the flicker this fixes).
  // `useDebouncedCallback` is stable and always calls the latest closure.
  const debouncedPersist = useDebouncedCallback(
    (wire: CopySettingsWire) => {
      updateUser({
        // `copy_settings` not yet in the generated schema (SCR-4468 BE
        // pending) — cast at the boundary.
        session_settings: {
          copy_settings: wire,
        } as NonNullable<Parameters<typeof updateUser>[0]['session_settings']>,
      });
    },
    PERSIST_DEBOUNCE_MS,
    { leading: false, trailing: true }
  );

  // Flush on unmount: the dropdown unmounts on close, and this close-flush
  // (not the timer) is the real coalesce point for a toggle-then-close.
  useEffect(() => () => debouncedPersist.flush(), [debouncedPersist]);

  const storedSettings = useMemo<CopySettings>(() => {
    if (!sessionSettings) return LOADING_FALLBACK;

    if (isImprovedCopyEnabled) {
      const wire = sessionSettings.copy_settings;
      if (wire) return wireToCopySettings(wire);
      // BE hasn't deployed SCR-4468 yet — derive from the legacy enum
      // so the UI still reflects the user's stored preference.
      return enumToToggles(sessionSettings.default_copy_type);
    }

    return enumToToggles(sessionSettings.default_copy_type);
  }, [isImprovedCopyEnabled, sessionSettings]);

  const settings = useMemo<CopySettings>(
    () =>
      areListAndTableTogglesHidden
        ? { ...storedSettings, ...FORCED_ON_WHILE_HIDDEN }
        : storedSettings,
    [areListAndTableTogglesHidden, storedSettings]
  );

  const updateSettings = useCallback(
    (next: Partial<CopySettings>) => {
      if (!isImprovedCopyEnabled) {
        // The legacy enum is the source of truth when the flag is off;
        // writes flow through `useSessionCopy.updateCopyPreference`.
        // We intentionally do nothing here so a stray caller can't
        // round-trip toggles → enum and drop the bullet-points signal.
        return;
      }

      // Merge against the cache (not the closed-over `settings`) so rapid
      // clicks accumulate even before a re-render refreshes the closure.
      const cachedUser = queryClient.getQueryData<UserResponse>([GET_USER]);
      if (!cachedUser) {
        // Invariant: the panel only renders once the user has loaded. The
        // optimistic write below bails; the debounced PATCH still persists.
        logger.warn('[copySettings] optimistic update ran with no cached user');
      }
      const cachedWire = (
        cachedUser?.session_settings as SessionSettingsWithCopy | undefined
      )?.copy_settings;
      // `storedSettings`, not the forced view: a flag-driven default must never
      // ride out on a write (B2) — the backend owns that default.
      const current = cachedWire ? wireToCopySettings(cachedWire) : storedSettings;
      const wire = copySettingsToWire({ ...current, ...next });

      // Cancel in-flight user refetches so none lands after and clobbers
      // the optimistic write (standard optimistic-update ordering).
      void queryClient.cancelQueries({ queryKey: [GET_USER] });

      // Optimistic write so the toggle reflects immediately; bail on an
      // empty cache rather than writing a partial user.
      queryClient.setQueryData([GET_USER], (oldData: UserResponse | undefined) =>
        oldData
          ? {
              ...oldData,
              session_settings: {
                ...oldData.session_settings,
                copy_settings: wire,
              } as SessionSettingsWithCopy,
            }
          : oldData
      );

      debouncedPersist(wire);
    },
    [storedSettings, isImprovedCopyEnabled, queryClient, debouncedPersist]
  );

  return {
    settings,
    updateSettings,
    isImprovedCopyEnabled,
    areListAndTableTogglesHidden,
  };
};
